import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  ConnectionLineType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { NodeToolbar } from './NodeToolbar';
import { NodeSettingsPanel } from './NodeSettingsPanel';
import { WorkflowNode } from './nodes/WorkflowNode';
import { useParams } from 'react-router-dom';
import workflowService from '../../services/workflowService';
import { useQuery } from '@tanstack/react-query';

const EDGE_COLOR = '#FF6A3A';

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: {
    stroke: EDGE_COLOR,
    strokeWidth: 2,
  },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: EDGE_COLOR,
    width: 20,
    height: 20,
  },
};

const nodeTypes = {
  workflowNode: WorkflowNode,
};

const startNode = {
  id: 'start-1',
  type: 'workflowNode',
  position: { x: 250, y: 100 },
  data: { label: 'Start', type: 'start' },
};

function generateNodesAndEdgesFromSteps(steps = []) {
  const nodes = [startNode];
  const edges = [];
  let previousNodeId = startNode.id;

  steps.forEach((step, index) => {
    const nodeId = `${step.type || 'step'}-${index}`;

    nodes.push({
      id: nodeId,
      type: 'workflowNode',
      position: { x: 250, y: 200 + index * 130 },
      data: {
        label: step.step_name || step.type || 'Step',
        type: step.type,
        ...step,
      },
    });

    edges.push({
      id: `edge-${previousNodeId}-${nodeId}`,
      source: previousNodeId,
      target: nodeId,
      ...defaultEdgeOptions,
    });

    previousNodeId = nodeId;
  });

  return { nodes, edges };
}

function getChainTailId(nodes, edges) {
  const start = nodes.find(n => n.data?.type === 'start' || n.id === 'start-1');
  if (!start) return nodes[nodes.length - 1]?.id;

  let currentId = start.id;
  const seen = new Set();
  while (currentId && !seen.has(currentId)) {
    seen.add(currentId);
    const next = edges.find(e => e.source === currentId);
    if (!next) break;
    currentId = next.target;
  }
  return currentId;
}

export const WorkflowCanvas = () => {
  const { id } = useParams();
  const [nodes, setNodes, onNodesChange] = useNodesState([startNode]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const hydratedForId = useRef(null);

  const workflowQuery = useQuery({
    queryKey: ['get-workflow-details', id],
    queryFn: () => workflowService.getWorkflowDetail(id),
    enabled: !!id,
    staleTime: 30_000,
  });

  const detail = workflowQuery?.data?.detail;

  const onConnect = useCallback(
    params => {
      setEdges(eds => addEdge({ ...params, ...defaultEdgeOptions }, eds));
    },
    [setEdges],
  );

  const onNodeClick = useCallback((_event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const addNode = useCallback(
    type => {
      const newId = `${type}-${Date.now()}`;

      // Use latest nodes+edges so the new step is chained and Save can walk the graph
      setNodes(nds => {
        setEdges(eds => {
          const tailId = getChainTailId(nds, eds);
          if (!tailId) return eds;
          return addEdge(
            {
              id: `edge-${tailId}-${newId}`,
              source: tailId,
              target: newId,
              ...defaultEdgeOptions,
            },
            eds,
          );
        });

        const tailId = getChainTailId(nds, edges);
        const tail = nds.find(n => n.id === tailId) || nds[nds.length - 1];

        return [
          ...nds,
          {
            id: newId,
            type: 'workflowNode',
            position: {
              x: tail?.position?.x ?? 250,
              y: (tail?.position?.y ?? 100) + 130,
            },
            data: { label: type.charAt(0).toUpperCase() + type.slice(1), type },
          },
        ];
      });
    },
    [edges, setNodes, setEdges],
  );

  const updateNodeData = useCallback(
    (nodeId, newData) => {
      setNodes(nds =>
        nds.map(node => (node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node)),
      );
      setSelectedNode(prev =>
        prev?.id === nodeId ? { ...prev, data: { ...prev.data, ...newData } } : prev,
      );
    },
    [setNodes],
  );

  // Load workflow graph when id/detail arrives — once per workflow id
  useEffect(() => {
    if (!id) return;

    if (hydratedForId.current !== id) {
      hydratedForId.current = null;
      setNodes([startNode]);
      setEdges([]);
      setSelectedNode(null);
    }

    if (!detail || workflowQuery.isFetching) return;
    if (hydratedForId.current === id) return;

    const { nodes: generatedNodes, edges: generatedEdges } = generateNodesAndEdgesFromSteps(detail.steps || []);
    setNodes(generatedNodes);
    setEdges(generatedEdges);
    hydratedForId.current = id;
  }, [id, detail, workflowQuery.isFetching, setNodes, setEdges]);

  return (
    <div className="flex-1 flex" style={{ minHeight: 0 }}>
      <div className="flex-1 relative" style={{ minHeight: 480 }}>
        {detail?.name && (
          <div className="absolute bottom-4 right-4 z-10">
            <div
              className="rounded-xl p-3 shadow-lg"
              style={{
                background: 'var(--card)',
                border: '1px solid var(--line)',
                boxShadow: 'var(--shadow-lg)',
              }}>
              <h2
                className="text-base font-semibold mb-0"
                style={{
                  maxWidth: '300px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'var(--ink)',
                }}>
                {detail.name}
              </h2>
            </div>
          </div>
        )}

        <NodeToolbar
          onAddNode={addNode}
          setSelectedNode={setSelectedNode}
          nodesData={nodes}
          edgesData={edges}
          workflowId={id}
          isEdit={detail}
        />

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionLineStyle={{
            stroke: EDGE_COLOR,
            strokeWidth: 2,
          }}
          fitView
          style={{ background: 'var(--paper)' }}>
          <Background color="rgba(10, 9, 8, 0.12)" gap={18} size={1} />
        </ReactFlow>
      </div>

      {selectedNode && selectedNode?.data?.type !== 'start' && (
        <NodeSettingsPanel node={selectedNode} onUpdateNode={updateNodeData} onClose={() => setSelectedNode(null)} />
      )}
    </div>
  );
};
